import { Typography } from '@material-ui/core';
import { operacaoStatusDescriptions } from 'common/genericDescriptions';
import { CompraVendaDocumento, CompraVendaStatus, OperacaoCommonStatus, OperacaoCompraVendaResponse } from 'common/genericTypes';
import { LgSpace, MdSpace, Section, Button, SmSpace } from 'm2m.components';
import React, { useState, useEffect, useCallback } from 'react';
import { formatDate } from 'utils/date';
import { addCpfCnpjMask } from 'utils/mask';
import { formatNumber, formatEightDecimalsCurrency } from 'utils/number';
import { RouteComponentProps, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from 'store';
import { GestaoComprovantesDrawer } from 'pages/operacoes/compraVenda/gestaoComprovantesDrawer';

type ComponentParams = { operacaoId: string; modulo?: string };
type ComponentState = { initialTab?: number };

type Props = RouteComponentProps<ComponentParams, {}, ComponentState>;

const DetalhesCompraVenda: React.FC<Props> = (props) => {
  const { operacaoId } = props.match.params;

  const [operacaoDetalhes, setOperacaoDetalhes] = useState<OperacaoCompraVendaResponse>();
  const [openGestaoComprovantesDrawer, setOpenGestaoComprovantesDrawer] = useState<boolean>(false);
  const [compraVendaDocumentos, setCompraVendaDocumentos] = useState<Array<CompraVendaDocumento>>([]);

  const dispatch = useDispatch();
  const history = useHistory();

  const getOperacaoCompraVendaDetalhes = useSelector(
    (state: RootState) => state.operacaoApi.getOperacaoCompraVendaDetalhes
  );
  const getCompraVendaDocumentos = useSelector((state: RootState) => state.operacaoApi.getCompraVendaDocumentos);

  const getOperacaoDetalhes = useCallback(async () => {
    try {
      const result = await getOperacaoCompraVendaDetalhes(dispatch, operacaoId, true);
      setOperacaoDetalhes(result);
    } catch {}
  }, [dispatch, getOperacaoCompraVendaDetalhes, operacaoId]);

  useEffect(() => {
    getOperacaoDetalhes();
  }, [getOperacaoDetalhes]);

  const goBack = () => history.push('/operacoes/gerenciar', { initialTab: 1 });

  const handleOpenGestaoComprovantes = async () => {
    try {
      const docs = await getCompraVendaDocumentos(dispatch, operacaoId, true);
      setCompraVendaDocumentos(docs);
    } catch {
      setCompraVendaDocumentos([]);
    }
    setOpenGestaoComprovantesDrawer(true);
  };

  const handleCloseGestaoComprovantes = () => {
    setOpenGestaoComprovantesDrawer(false);
    setCompraVendaDocumentos([]);
  };

  const handleGestaoComprovantesUploadSuccess = async () => {
    try {
      const docs = await getCompraVendaDocumentos(dispatch, operacaoId, true);
      setCompraVendaDocumentos(docs);
    } catch {
      setCompraVendaDocumentos([]);
    }
  };

  return (
    <div style={{ marginRight: 40 }}>
      <Typography variant="h1">{'Compra e venda'}</Typography>
      <SmSpace />

      <div style={{ display: 'flex', gap: '16px' }}>
        <Button onClick={goBack} variant="contained" color="primary">
          {'Voltar para a página anterior'}
        </Button>
        <Button onClick={handleOpenGestaoComprovantes} variant="outlined" color="primary">
          {'Gestão de Comprovantes'}
        </Button>
      </div>

      <LgSpace />
      {operacaoDetalhes && (
        <>
          <Section title={'Detalhes da Movimentação'} defaultGrid>
            <div>
              <Typography variant="body2">{'Código:'}</Typography>
              <Typography variant="body1">{operacaoDetalhes.instrumentoFinanceiro.codigo}</Typography>
            </div>
            <div>
              <Typography variant="body2">{'Data da Solicitação:'}</Typography>
              <Typography variant="body1">{formatDate(operacaoDetalhes.dataDeCriacao)}</Typography>
            </div>
            <div>
              <Typography variant="body2">{'Quantidade:'}</Typography>
              <Typography variant="body1">{formatNumber(operacaoDetalhes.compraVenda.quantidade, { minimumFractionDigits: 0, maximumFractionDigits: 8 })}</Typography>
            </div>
            <div>
              <Typography variant="body2">{'PU:'}</Typography>
              <Typography variant="body1">
                {formatEightDecimalsCurrency(operacaoDetalhes.compraVenda.precoUnitario)}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{'Status:'}</Typography>
              <Typography variant="body1">{operacaoStatusDescriptions[operacaoDetalhes.status]}</Typography>
            </div>
            {(operacaoDetalhes.status === CompraVendaStatus.AceiteOrigemRealizado ||
              operacaoDetalhes.status === CompraVendaStatus.AceiteDestinoRealizado) && (
              <div>
                <Typography variant="body2">{'Aprovação Bilateral:'}</Typography>
                <Typography variant="body1">
                  {operacaoDetalhes.status === CompraVendaStatus.AceiteOrigemRealizado
                    ? 'Custodiante origem já aprovou. Aguardando aceite do custodiante destino.'
                    : 'Custodiante destino já aprovou. Aguardando aceite do custodiante origem.'}
                </Typography>
              </div>
            )}
            {operacaoDetalhes.status === CompraVendaStatus.LiquidacaoEmProcessamento && (
              <div>
                <Typography variant="body2">{'Liquidação:'}</Typography>
                <Typography variant="body1">
                  {'Liquidação sendo processada pela câmara (B3). Aguardando confirmação.'}
                </Typography>
              </div>
            )}
            {(operacaoDetalhes.status === OperacaoCommonStatus.Reprovada || operacaoDetalhes.status === CompraVendaStatus.Cancelada) && operacaoDetalhes.justificativa && (
              <div>
                <Typography variant="body2">{'Motivo:'}</Typography>
                <Typography variant="body1">{operacaoDetalhes.justificativa}</Typography>
              </div>
            )}
            <div>
              <Typography variant="body2">{'Custodiante:'}</Typography>
              <Typography variant="body1">{operacaoDetalhes.custodiante.razaoSocial}</Typography>
            </div>
            <div>
              <Typography variant="body2">{'CPF/CNPJ do Vendedor:'}</Typography>
              <Typography variant="body1">
                {addCpfCnpjMask(operacaoDetalhes.compraVenda?.investidorOrigem?.cpfCnpj)}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{'Conta Depósito do Vendedor:'}</Typography>
              <Typography variant="body1">{operacaoDetalhes.compraVenda?.investidorOrigem?.contaDeposito}</Typography>
            </div>
            <div>
              <Typography variant="body2">{'CPF/CNPJ do Comprador:'}</Typography>
              <Typography variant="body1">
                {addCpfCnpjMask(operacaoDetalhes.compraVenda?.investidorDestino?.cpfCnpj)}
              </Typography>
            </div>
            <div>
              <Typography variant="body2">{'Conta Depósito do Comprador:'}</Typography>
              <Typography variant="body1">{operacaoDetalhes.compraVenda?.investidorDestino?.contaDeposito}</Typography>
            </div>
            <MdSpace />
          </Section>
          <LgSpace />
        </>
      )}

      <GestaoComprovantesDrawer
        operacaoId={operacaoId}
        codigoInstrumento={operacaoDetalhes?.instrumentoFinanceiro?.codigo}
        comprovantes={compraVendaDocumentos}
        opened={openGestaoComprovantesDrawer}
        onClose={handleCloseGestaoComprovantes}
        onUploadSuccess={handleGestaoComprovantesUploadSuccess}
      />
    </div>
  );
};

export default DetalhesCompraVenda;
